import React, { useCallback, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { Select, MenuItem, Typography, Button, Paper, Divider, IconButton, Tooltip, FormControl, InputLabel, Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Autocomplete } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import constants from '../utilities/constants';
import debounce from 'lodash/debounce';
import {StateManager} from '../utilities/stateManager';
import { useStackZIndex } from '../utilities/ModalStackContext';

const ServiceEditForm = ({ service, onUpdate, onDelete }) => {
  const popperZ = useStackZIndex();
  const statusOptions = constants.service_statuses.map(status => ({
    value: status,
    label: status.toProperCase()
  }));

  // Options are keyed by mechanic id (not name) so renaming a mechanic never
  // breaks selection or cost math. `value` === id; `label` is the current name.
  let mechanicOptions = constants.mechanics.filter(mechanic => mechanic.is_active).map(mechanic => ({
    id: mechanic.id,
    value: mechanic.id,
    label: mechanic.name,
    rate: mechanic.rate
  }))

  mechanicOptions.unshift({
    id: 'none',
    value: 'none',
    label: 'None',
    rate: 0
  });

  // Resolve the selected mechanic by id. Falls back to the legacy `service.mechanic`
  // name for older records written before the id-based paradigm.
  const selectedMechanic = useMemo(() => {
    if (service.mechanicID) {
      return mechanicOptions.find(m => m.id === service.mechanicID);
    }
    return mechanicOptions.find(m => m.label === service.mechanic);
  }, [service.mechanicID, service.mechanic]);

  // Rates are effective-dated: a line is priced at the rate in effect on its
  // status_time day (today when it has none).
  const rateAt = (mechanic, day) => (
    !mechanic || mechanic.id === 'none'
      ? 0
      : constants.rateOn(mechanic.id, day || moment().format('YYYY/MM/DD'))
  );

  // Memoize the cost calculation
  const calculatedCost = useMemo(() => 
    rateAt(selectedMechanic, service.status_time) * (service.time || 0),
    [selectedMechanic, service.time, service.status_time]
  );

  // Debounced update function with toast notification
  const debouncedUpdate = useCallback(
    debounce((updates) => {
      onUpdate(updates);
      StateManager.setAlertAndOpen('Service updated successfully', 'success');
    }, 300),
    [onUpdate]
  );

  // Handle text field changes with debouncing
  const handleTextChange = (field) => (e) => {
    const value = e.target.value;
    // Update the local state immediately for responsive UI
    e.target.value = value;
    // Debounce the actual update
    debouncedUpdate({ [field]: value });
  };

  // Handle number field changes with debouncing
  const handleNumberChange = (field) => (e) => {
    const value = parseFloat(e.target.value) || 0;
    // Update the local state immediately for responsive UI
    e.target.value = value;
    // Calculate cost if hours are changing
    const updates = { [field]: value };
    if (field === 'time' && selectedMechanic) {
      updates.rate = rateAt(selectedMechanic, service.status_time);
      updates.cost = updates.rate * value;
    }
    // Debounce the actual update
    debouncedUpdate(updates);
  };

  // Handle select changes immediately (no debounce needed)
  const handleSelectChange = (field) => (e) => {
    const value = e.target.value;
    let updates = { [field]: value };

    if (field === 'mechanic') {
      // `value` is now the mechanic id. Persist id-based, and derive the name/rate
      // from the current roster so a later rename or raise stays consistent. The
      // legacy `mechanic` (name) field is kept in sync for name-based consumers.
      const newMechanic = mechanicOptions.find(m => m.id === value);
      const isNone = !newMechanic || newMechanic.id === 'none';
      const rate = rateAt(newMechanic, service.status_time);
      updates = {
        mechanicID: isNone ? '' : newMechanic.id,
        mechanic: isNone ? '' : newMechanic.label,
        rate,
        cost: rate * (service.time || 0),
      };
    } else if (field === 'status') {
      // Always update status_time when status changes, and re-price at the
      // rate in effect on the new status_time day.
      updates.status_time = moment().format('YYYY/MM/DD');
      if (selectedMechanic && selectedMechanic.id !== 'none') {
        updates.rate = rateAt(selectedMechanic, updates.status_time);
        updates.cost = updates.rate * (service.time || 0);
      }
    }
    onUpdate(updates);
    StateManager.setAlertAndOpen('Service updated successfully', 'success');
  };

  // Handle tag changes (freeSolo Autocomplete). Commits on select/Enter and on blur.
  const handleTagChange = (raw) => {
    const v = (raw || '').trim();
    const current = service.tag || '';
    if (v === current) return;     // no change
    if (!v && !current) return;    // both empty — nothing to do
    onUpdate({ tag: v || 'Other' });
    StateManager.setAlertAndOpen('Service updated successfully', 'success');
  };

  // Handle copy ID to clipboard
  const handleCopyId = () => {
    navigator.clipboard.writeText(service.id);
  };

  // Handle date changes
  const handleDateChange = (field) => (date) => {
    const formattedDate = date ? moment(date).format('YYYY/MM/DD') : null;
    const updates = { [field]: formattedDate };
    // status_time decides the rate, so moving it re-prices the line.
    if (field === 'status_time' && selectedMechanic && selectedMechanic.id !== 'none') {
      updates.rate = rateAt(selectedMechanic, formattedDate);
      updates.cost = updates.rate * (service.time || 0);
    }
    onUpdate(updates);
    StateManager.setAlertAndOpen('Service updated successfully', 'success');
  };

  // Handle delete - show prompt first, then call onDelete if provided
  const handleDelete = () => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }
    
    if (onDelete) {
      onDelete(service.id);
    } else {
      StateManager.setAlertAndOpen('Delete handler not configured', 'error');
    }
  };

  const handleApproveTime = () => {
    const rate = rateAt(selectedMechanic, service.status_time);
    const logEntry = {
      action: 'approved',
      proposed_time: service.proposed_time,
      by: StateManager.userName,
      userId: StateManager.userID,
      at: moment().format('YYYY/MM/DD HH:mm'),
    };
    onUpdate({
      time: service.proposed_time,
      rate,
      cost: rate * service.proposed_time,
      proposed_time: null,
      time_submission_status: 'approved',
      time_submission_log: [...(service.time_submission_log || []), logEntry],
    });
    StateManager.setAlertAndOpen('Time approved', 'success');
  };

  const handleRejectTime = () => {
    const logEntry = {
      action: 'rejected',
      proposed_time: service.proposed_time,
      by: StateManager.userName,
      userId: StateManager.userID,
      at: moment().format('YYYY/MM/DD HH:mm'),
    };
    onUpdate({
      proposed_time: null,
      time_submission_status: 'rejected',
      time_submission_log: [...(service.time_submission_log || []), logEntry],
    });
    StateManager.setAlertAndOpen('Time submission rejected', 'warning');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Grid container spacing={4}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 0 }}>
                    Service Details
                  </Typography>
                  {service.declined && (
                    <Chip label="Declined" color="error" size="small" sx={{ fontWeight: 600 }} />
                  )}
                </Box>
              </Grid>
              <Grid item>
                <Grid container spacing={1} alignItems="center">
                  {StateManager.isBackoffice() && (
                    <Grid item>
                      <Button
                        size="small"
                        variant={service.declined ? 'contained' : 'outlined'}
                        color={service.declined ? 'success' : 'error'}
                        onClick={() => {
                          onUpdate({ declined: !service.declined });
                          StateManager.setAlertAndOpen(
                            service.declined ? 'Service reinstated' : 'Service declined — excluded from accounting',
                            service.declined ? 'success' : 'warning'
                          );
                        }}
                      >
                        {service.declined ? 'Reinstate' : 'Decline'}
                      </Button>
                    </Grid>
                  )}
                  <Grid item>
                    <Tooltip title="Copy Service ID">
                      <IconButton onClick={handleCopyId} size="small">
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  {(StateManager.isManager() || StateManager.isGrantedLineMechanic(service)) && (
                    <Grid item>
                      <Tooltip title="Delete Service">
                        <IconButton 
                          onClick={handleDelete} 
                          size="small"
                          color="error"
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: 'error.light',
                              color: 'error.contrastText'
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          {/* Basic Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Service Name"
                  defaultValue={service.name || ''}
                  onChange={handleTextChange('name')}
                  disabled={!StateManager.canEditServiceLine(service)}
                  InputProps={{ 
                    style: { fontSize: 18, fontWeight: 'bold' },
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={service.status || 'pending'}
                    onChange={handleSelectChange('status')}
                    disabled={!StateManager.isManager()}
                    sx={{ 
                      '& .MuiSelect-select': { py: 1.5 },
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    {statusOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Mechanic</InputLabel>
                  <Select
                    label="Mechanic"
                    value={selectedMechanic ? selectedMechanic.id : ''}
                    onChange={handleSelectChange('mechanic')}
                    disabled={!StateManager.isBackoffice()}
                    sx={{ 
                      '& .MuiSelect-select': { py: 1.5 },
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    {mechanicOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Status Time"
                  value={service.status_time ? moment(service.status_time) : null}
                  onChange={handleDateChange('status_time')}
                  disabled={!StateManager.isManager()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        '&:hover': { backgroundColor: 'action.hover' }
                      }
                    },
                    popper: { sx: { zIndex: popperZ } },
                    desktopPaper: { sx: { zIndex: popperZ } },
                    mobilePaper: { sx: { zIndex: popperZ } },
                    dialog: { sx: { zIndex: popperZ } },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={constants.service_tags}
                  value={service.tag || ''}
                  onChange={(e, newValue) => handleTagChange(newValue)}
                  slotProps={{ popper: { sx: { zIndex: popperZ } } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tag (group)"
                      helperText="Used to group services on the estimate"
                      onBlur={(e) => handleTagChange(e.target.value)}
                      sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Proposed Time Review */}
          {service.time_submission_status === 'pending' && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'warning.main',
                  borderRadius: 1,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(237,108,2,0.12)' : 'warning.50',
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Chip label="Pending Approval" size="small" color="warning" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Mechanic Submitted Time
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: StateManager.canApproveHours() ? 1.5 : 0 }}>
                  Proposed: <strong>{service.proposed_time} hrs</strong>
                  {' '}(current: {service.time || 0} hrs)
                </Typography>
                {StateManager.canApproveHours() && (
                  <Box display="flex" gap={1}>
                    <Button variant="contained" color="success" size="small" onClick={handleApproveTime}>
                      Approve
                    </Button>
                    <Button variant="outlined" color="error" size="small" onClick={handleRejectTime}>
                      Reject
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}

          {/* Financial Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              Financial Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Hours"
                  defaultValue={service.time || 0}
                  onChange={handleNumberChange('time')}
                  disabled={!StateManager.isBackoffice()}
                  InputProps={{
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center',
                    backgroundColor: 'action.hover'
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Calculated Cost
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    ${calculatedCost.toFixed(2)}
                  </Typography>
                  {selectedMechanic && (
                    <Typography variant="caption" color="text.secondary">
                      Based on {selectedMechanic.label}'s rate of ${selectedMechanic.rate}/hr
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Additional Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              Additional Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Notes"
                  defaultValue={service.notes || ''}
                  onChange={handleTextChange('notes')}
                  InputProps={{
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
              {StateManager.isAdmin() && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Order"
                    defaultValue={service.order || ''}
                    onChange={handleTextChange('order')}
                    InputProps={{
                      sx: { '&:hover': { backgroundColor: 'action.hover' } }
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* Time Submission History */}
          {(service.time_submission_log || []).length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                Time Submission History
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Proposed Hrs</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...(service.time_submission_log)].reverse().map((entry, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Chip
                          label={entry.action}
                          size="small"
                          color={['approved', 'auto-approved'].includes(entry.action) ? 'success' : entry.action === 'rejected' ? 'error' : 'warning'}
                          sx={{ fontSize: '0.7rem', textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>{entry.proposed_time}</TableCell>
                      <TableCell>{entry.by}</TableCell>
                      <TableCell>{entry.at}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          )}
        </Grid>
      </Paper>
    </LocalizationProvider>
  );
};

export default ServiceEditForm;

